import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  ListItemIcon,
  Radio,
  Dialog,
  DialogContent,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { 
  Search as SearchIcon, 
  Refresh as RefreshIcon,
  Close as CloseIcon,
  FilterAlt,
  DateRange,
  Today,
  CalendarMonth,
  FileDownload,
} from '@mui/icons-material';
import { getProjectsPaginated } from '../../services/project';
import { getProjectReport } from '../../services/reports';
import ExportButton from '../../components/ExportButton/ExportButton';
import RefreshButton from '../../components/RefreshButton/RefreshButton';
import FormatBadge from '../../components/FormatBadge/FormatBadge';
import { showSnackbar } from '../../components/Snackbar';
import { exportAsPDF, exportAsCSV, exportAsExcel } from '../../utils/importExport';
import AlertModal from '../../components/AlertModal';
import MoreFilters from '../../components/MoreFilters/MoreFilters';


const BranchWiseSummary = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    date: '',
    branch: '',
    searchTerm: ''
  });
  const [sortBy, setSortBy] = useState('');
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);

  const sortMapping = {
    totalBookingAmountDesc: { apiSortBy: 'totalBookingAmount', apiSortOrder: 'desc' },
    totalBookingAmountAsc:  { apiSortBy: 'totalBookingAmount', apiSortOrder: 'asc'  },
    totalPaidAmountDesc:    { apiSortBy: 'totalPaidAmount',    apiSortOrder: 'desc' },
    totalPaidAmountAsc:     { apiSortBy: 'totalPaidAmount',    apiSortOrder: 'asc'  },
    totalDueAmountDesc:     { apiSortBy: 'totalDueAmount',     apiSortOrder: 'desc' },
    totalDueAmountAsc:      { apiSortBy: 'totalDueAmount',     apiSortOrder: 'asc'  },
    totalBookingsDesc:      { apiSortBy: 'totalBookings',      apiSortOrder: 'desc' },
    totalBookingsAsc:       { apiSortBy: 'totalBookings',      apiSortOrder: 'asc'  },
  };

  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [totalProjects, setTotalProjects] = useState(0);
  const [loadingReport, setLoadingReport] = useState(false);

  // Export Dialog States
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportType, setExportType] = useState('');
  const [exportDateMode, setExportDateMode] = useState('current');
  const [exportFromDate, setExportFromDate] = useState('');
  const [exportToDate, setExportToDate] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [alertModal, setAlertModal] = useState({ open: false, title: 'Alert', message: '' });
  const closeAlert = () => setAlertModal({ open: false, title: 'Alert', message: '' });


  // Helper function to format currency amounts with proper Indian number formatting
  const formatCurrency = (amount) => {
    return Number(amount || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      useGrouping: true
    });
  };

  // Helper function to format regular numbers with Indian formatting
  const formatNumber = (number) => {
    return Number(number || 0).toLocaleString('en-IN');
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchReportData(newPage + 1, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchReportData(1, newRowsPerPage);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Fetch projects for dropdown
  const fetchProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      const response = await getProjectsPaginated({ page: 1, limit: 100 });
      const list = Array.isArray(response?.data) ? response.data : [];
      const formatted = list.map((project) => ({
        id: project._id || project.id,
        name: project.projectName || project.name || 'Untitled Project',
        projectNo: project.projectNo || '',
      }));
      setProjects(formatted);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  // Fetch report data
  const fetchReportData = useCallback(async (pageNum = 1, limit = rowsPerPage, overrides = {}) => {
    setLoadingReport(true);
    try {
      const activeSortBy = overrides.sortBy !== undefined ? overrides.sortBy : sortBy;
      const { apiSortBy, apiSortOrder } = (activeSortBy && sortMapping[activeSortBy]) || {};

      const activeDate     = overrides.date     !== undefined ? overrides.date     : filters.date;
      const activeFromDate = overrides.fromDate  !== undefined ? overrides.fromDate : filters.fromDate;
      const activeToDate   = overrides.toDate    !== undefined ? overrides.toDate   : filters.toDate;
      const activeBranch   = overrides.branch    !== undefined ? overrides.branch   : filters.branch;

      const params = {
        page: pageNum,
        limit,
        fromDate:  activeDate ? activeDate : activeFromDate,
        toDate:    activeDate ? activeDate : activeToDate,
        sortBy:    apiSortBy,
        sortOrder: apiSortOrder,
      };
      if (activeBranch) params.projectId = activeBranch;
      Object.keys(params).forEach(key => { if (!params[key]) delete params[key]; });

      const response = await getProjectReport(params);
      if (response?.success && Array.isArray(response.data)) {
        const mapped = response.data.map((item, index) => ({
          sno: (pageNum - 1) * limit + index + 1,
          branchName: item.projectName || 'N/A',
          totalBookings: item.totalBookings ?? 0,
          totalAmount: item.totalBookingAmount ?? 0,
          paidAmount: item.totalPaidAmount ?? 0,
          dueAmount: item.totalDueAmount ?? 0,
          completedBookings: item.bookedInventory ?? 0,
          pendingBookings: item.availableInventory ?? 0,
        }));
        setReportData(mapped);
        setTotalProjects(response.pagination?.totalProjects || mapped.length);
      } else {
        setReportData([]);
        setTotalProjects(0);
      }
    } catch (error) {
      console.error('Error fetching project report:', error);
      setReportData([]);
      setTotalProjects(0);
    } finally {
      setLoadingReport(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.date, filters.fromDate, filters.toDate, filters.branch, sortBy, rowsPerPage]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Load report data on mount and whenever filters change
  useEffect(() => {
    fetchReportData(1, rowsPerPage);
    setPage(0);
  }, [fetchReportData]);

  const handleSearch = () => {
    // Reset to first page whenever a manual search is triggered
    setPage(0);
    fetchReportData(1, rowsPerPage);
  };

  const handleReset = () => {
    setFilters({
      fromDate: '',
      toDate: '',
      date: '',
      branch: '',
      searchTerm: ''
    });
    setSortBy('');
    setPage(0);
    fetchReportData(1, rowsPerPage, {
      fromDate: '', toDate: '', date: '', branch: '', sortBy: ''
    });
  };

  // Export functions
  const buildExportRows = () => {
    return reportData.map((item) => ({
      'S.No': item.sno,
      'Branch Name': item.branchName,
      'Total Bookings': item.totalBookings,
      'Total Amount (Rs.)': item.totalAmount,
      'Paid Amount (Rs.)': item.paidAmount,
      'Due Amount (Rs.)': item.dueAmount,
      'Completed': item.completedBookings,
      'Pending': item.pendingBookings
    }));
  };

  const handleOpenExportDialog = (type) => {
    setExportType(type);
    setExportDialogOpen(true);
    setExportFromDate(filters.fromDate || '');
    setExportToDate(filters.toDate || '');
    setExportDateMode('current');
  };

  const handleCloseExportDialog = () => {
    setExportDialogOpen(false);
    setExportType('');
    setExportLoading(false);
  };

  const handleConfirmExport = async () => {
    setExportLoading(true);
    try {
      const params = {
        export: 'true',
      };

      if (filters.branch) {
        params.projectId = filters.branch;
      }

      // Apply date filters only if NOT in "All Dates (Full History)" mode
      if (exportDateMode !== 'all') {
        if (exportDateMode === 'current') {
          if (filters.fromDate) params.fromDate = filters.fromDate;
          if (filters.toDate) params.toDate = filters.toDate;
        } else if (exportDateMode === 'custom') {
          if (exportFromDate) params.fromDate = exportFromDate;
          if (exportToDate) params.toDate = exportToDate;
        } else if (exportDateMode === 'single') {
          if (exportFromDate) {
            params.fromDate = exportFromDate;
            params.toDate = exportFromDate;
          }
        }
      }

      const response = await getProjectReport(params);
      if (response?.success && Array.isArray(response.data)) {
        const mapped = response.data.map((item, index) => ({
          sno: index + 1,
          branchName: item.projectName || 'N/A',
          totalBookings: item.totalBookings ?? 0,
          totalAmount: item.totalBookingAmount ?? 0,
          paidAmount: item.totalPaidAmount ?? 0,
          dueAmount: item.totalDueAmount ?? 0,
          completedBookings: item.bookedInventory ?? 0,
          pendingBookings: item.availableInventory ?? 0,
        }));

        if (mapped.length === 0) {
          setAlertModal({ open: true, title: 'Warning', message: 'No data found for the selected filter/date range.' });
          setExportLoading(false);
          return;
        }

        const rows = mapped.map((item) => ({
          'S.No': item.sno,
          'Branch Name': item.branchName,
          'Total Bookings': item.totalBookings,
          'Total Amount (Rs.)': item.totalAmount,
          'Paid Amount (Rs.)': item.paidAmount,
          'Due Amount (Rs.)': item.dueAmount,
          'Completed': item.completedBookings,
          'Pending': item.pendingBookings
        }));

        const exportTotals = {
          bookings: mapped.reduce((sum, item) => sum + Number(item.totalBookings || 0), 0),
          amount: mapped.reduce((sum, item) => sum + Number(item.totalAmount || 0), 0),
          paid: mapped.reduce((sum, item) => sum + Number(item.paidAmount || 0), 0),
          due: mapped.reduce((sum, item) => sum + Number(item.dueAmount || 0), 0),
        };

        const fileName = `branch_wise_summary_${new Date().toISOString().split('T')[0]}`;

        if (exportType === 'csv') {
          exportAsCSV({ rows, fileNameBase: fileName });
        } else if (exportType === 'pdf') {
          const rowsForPdf = [...rows, {
            'S.No': '',
            'Branch Name': 'Total',
            'Total Bookings': exportTotals.bookings,
            'Total Amount (Rs.)': `Rs. ${formatCurrency(exportTotals.amount)}`,
            'Paid Amount (Rs.)': `Rs. ${formatCurrency(exportTotals.paid)}`,
            'Due Amount (Rs.)': `Rs. ${formatCurrency(exportTotals.due)}`,
            'Completed': '',
            'Pending': ''
          }];

          const formattedRows = rowsForPdf.map(r => {
            if (r['Branch Name'] === 'Total') return r;
            return {
              ...r,
              'Total Bookings': formatNumber(r['Total Bookings']),
              'Total Amount (Rs.)': `Rs. ${formatCurrency(r['Total Amount (Rs.)'])}`,
              'Paid Amount (Rs.)': `Rs. ${formatCurrency(r['Paid Amount (Rs.)'])}`,
              'Due Amount (Rs.)': `Rs. ${formatCurrency(r['Due Amount (Rs.)'])}`,
              'Completed': formatNumber(r['Completed']),
              'Pending': formatNumber(r['Pending'])
            };
          });

          exportAsPDF({
            rows: formattedRows,
            title: 'Branch Wise Summary Report',
            fileNameBase: fileName,
            orientation: 'l'
          });
        } else if (exportType === 'excel') {
          const rowsForExcel = [...rows, {
            'S.No': '',
            'Branch Name': 'Total',
            'Total Bookings': exportTotals.bookings,
            'Total Amount (Rs.)': exportTotals.amount,
            'Paid Amount (Rs.)': exportTotals.paid,
            'Due Amount (Rs.)': exportTotals.due,
            'Completed': '',
            'Pending': ''
          }];

          exportAsExcel({
            rows: rowsForExcel,
            sheetName: 'Branch Summary',
            fileNameBase: fileName
          });
        }

        handleCloseExportDialog();
      } else {
        setAlertModal({ open: true, title: 'Warning', message: 'No data found for the selected filter/date range.' });
      }
    } catch (error) {
      console.error('Export failed:', error);
      showSnackbar({ type: 'error', message: 'Export failed. Please try again.' });
    } finally {
      setExportLoading(false);
    }
  };

  const totals = {
    bookings: reportData.reduce((sum, item) => sum + Number(item.totalBookings || 0), 0),
    amount: reportData.reduce((sum, item) => sum + Number(item.totalAmount || 0), 0),
    paid: reportData.reduce((sum, item) => sum + Number(item.paidAmount || 0), 0),
    due: reportData.reduce((sum, item) => sum + Number(item.dueAmount || 0), 0),
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
            Branch Wise Summary
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Comprehensive overview of branch performance and financial metrics
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
          <ExportButton 
            onExportPdf={() => handleOpenExportDialog('pdf')} 
            onExportCsv={() => handleOpenExportDialog('csv')} 
            onExportExcel={() => handleOpenExportDialog('excel')} 
          />
          <RefreshButton
            onClick={() => fetchReportData(page + 1, rowsPerPage)}
            loading={loadingReport}
            sx={{
              fontWeight: 600,
              px: 2,
              fontSize: '15px',
              borderRadius: 2,
              height: { xs: 'auto', sm: 40 }
            }}
          />
        </Box>
      </Box>

      {/* Summary Cards */}
      {/* <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)', 
            borderRadius: 2,
            backgroundColor: 'primary.main',
            color: 'white'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Business sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {formatNumber(filteredData.length)}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Branches
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)', 
            borderRadius: 2,
            backgroundColor: 'error.main',
            color: 'white'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUp sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {formatNumber(totals.bookings)}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Bookings
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)', 
            borderRadius: 2,
            backgroundColor: 'info.main',
            color: 'white'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CheckCircle sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    ₹{formatCurrency(totals.paid)}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Paid Amount
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)', 
            borderRadius: 2,
            backgroundColor: 'success.main',
            color: 'white'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Schedule sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    ₹{formatCurrency(totals.due)}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Due Amount
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid> */}

      {/* Filters */}
      <Card sx={{ mb: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#536DFE' }}>
            Project Filters
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="project-select-label" shrink>Project</InputLabel>
                <Select
                  id="project-select"
                  labelId="project-select-label"
                  value={filters.branch}
                  onChange={(e) => handleFilterChange('branch', e.target.value)}
                  label="Project"
                  sx={{ borderRadius: 2 }}
                  disabled={loadingProjects}
                  displayEmpty
                  renderValue={(value) => {
                    if (!value) return 'All Projects';
                    const project = projects.find((p) => p.id === value);
                    return project
                      ? `${project.name}${project.projectNo ? ` (#${project.projectNo})` : ''}`
                      : 'All Projects';
                  }}
                >
                  <MenuItem value=""><em>All Projects</em></MenuItem>
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.name}{project.projectNo ? ` (#${project.projectNo})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                    height: '40px'
                  }}
                >
                  Search
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleReset}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                    height: '40px'
                  }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <MoreFilters
                expanded={moreFiltersOpen}
                onToggle={() => setMoreFiltersOpen(!moreFiltersOpen)}
                sortBy={sortBy}
                onChangeSortBy={setSortBy}
                type="branchWiseSummary"
                height="40px"
                onApply={() => {
                  setPage(0);
                  fetchReportData(1, rowsPerPage);
                }}
                onReset={() => {
                  setSortBy('');
                  setFilters(prev => ({ ...prev, date: '', fromDate: '', toDate: '' }));
                  fetchReportData(1, rowsPerPage, { sortBy: '', date: '', fromDate: '', toDate: '' });
                }}
              >
                {/* Single Date */}
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Single Date"
                  InputLabelProps={{ shrink: true }}
                  value={filters.date}
                  onChange={(e) => setFilters(prev => ({ ...prev, date: e.target.value, fromDate: '', toDate: '' }))}
                />

                {/* Date Range */}
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="From Date"
                      InputLabelProps={{ shrink: true }}
                      value={filters.fromDate}
                      onChange={(e) => setFilters(prev => ({ ...prev, fromDate: e.target.value, date: '' }))}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="To Date"
                      InputLabelProps={{ shrink: true }}
                      value={filters.toDate}
                      onChange={(e) => setFilters(prev => ({ ...prev, toDate: e.target.value, date: '' }))}
                    />
                  </Grid>
                </Grid>
              </MoreFilters>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#536DFE' }}>
            Project Summary Details
          </Typography>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#536DFE' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>S.No</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Project Name</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'right' }}>Total Bookings</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'right' }}>Total Amount (Rs.)</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'right' }}>Paid Amount (Rs.)</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'right' }}>Due Amount (Rs.)</TableCell>
                  {/* <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'right' }}>Completed</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'right' }}>Pending</TableCell> */}
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingReport ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        Loading report...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : reportData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No project data found for the selected filters
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  reportData.map((row) => (
                    <TableRow
                      key={row.sno}
                      hover
                      sx={{
                        '&:nth-of-type(odd)': { backgroundColor: '#f8f9fa' },
                        '&:hover': { backgroundColor: '#e3f2fd' }
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>{row.sno}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {row.branchName}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, textAlign: 'right', fontFamily: 'monospace', color: '#1976d2' }}>
                        {formatNumber(row.totalBookings)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, textAlign: 'right', fontFamily: 'monospace', color: '#2e7d32' }}>
                        Rs. {formatCurrency(row.totalAmount)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, textAlign: 'right', fontFamily: 'monospace', color: '#1976d2' }}>
                        Rs. {formatCurrency(row.paidAmount)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, textAlign: 'right', fontFamily: 'monospace', color: '#d32f2f' }}>
                        Rs. {formatCurrency(row.dueAmount)}
                      </TableCell>
                      {/* <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>
                        {formatNumber(row.completedBookings)}
                      </TableCell> */}
                      {/* <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>
                        {formatNumber(row.pendingBookings)}
                      </TableCell> */}
                    </TableRow>
                  ))
                )}
                {reportData.length > 0 && !loadingReport && (
                  <TableRow sx={{ backgroundColor: '#f0f0f0', fontWeight: 'bold' }}>
                    <TableCell colSpan={2} sx={{ fontWeight: 700, fontSize: '16px' }}>
                      <strong>Total</strong>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, textAlign: 'right', fontFamily: 'monospace', fontSize: '16px', color: '#1976d2' }}>
                      <strong>{formatNumber(totals.bookings)}</strong>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, textAlign: 'right', fontFamily: 'monospace', fontSize: '16px', color: '#2e7d32' }}>
                      <strong>Rs. {formatCurrency(totals.amount)}</strong>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, textAlign: 'right', fontFamily: 'monospace', fontSize: '16px', color: '#1976d2' }}>
                      <strong>Rs. {formatCurrency(totals.paid)}</strong>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, textAlign: 'right', fontFamily: 'monospace', fontSize: '16px', color: '#d32f2f' }}>
                      <strong>Rs. {formatCurrency(totals.due)}</strong>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            
            component="div"
            count={totalProjects}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              '& .MuiTablePagination-toolbar': {
                backgroundColor: '#f8f9fa',
              }
            }}
          />
        </CardContent>
      </Card>

      {/* Export Date Range Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={handleCloseExportDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 16px 48px rgba(83, 109, 254, 0.15), 0 2px 12px rgba(0,0,0,0.06)',
          }
        }}
      >
        {/* Gradient Header */}
        <Box sx={{
          background: 'linear-gradient(135deg, #536DFE 0%, #3d5afe 50%, #304ffe 100%)',
          px: 2.5, py: 1.8,
          position: 'relative', overflow: 'hidden',
        }}>
          <Box sx={{ position: 'absolute', top: -15, right: -15, width: 55, height: 55, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)' }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <Box sx={{
                width: 34, height: 34, borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.18)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <FileDownload sx={{ color: '#fff', fontSize: 18 }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem', lineHeight: 1.2 }}>
                  Export Branch Wise Summary
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', mt: 0.2 }}>
                  Download your report data
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleCloseExportDialog} size="small" sx={{ color: 'rgba(255,255,255,0.8)', p: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
          {/* Format Badge */}
          <FormatBadge type={exportType} />

          {/* Section label */}
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#555', mb: 1, textTransform: 'uppercase', letterSpacing: 0.6 }}>
            Select Date Range
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
            {/* Option 1: Current Filters */}
            <Box
              onClick={() => setExportDateMode('current')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'current' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'current' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'current' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <FilterAlt sx={{ fontSize: 16, color: exportDateMode === 'current' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  Current Filters
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {filters.fromDate && filters.toDate
                    ? `${filters.fromDate} → ${filters.toDate}`
                    : filters.fromDate ? `From ${filters.fromDate}` : filters.toDate ? `Till ${filters.toDate}` : 'All dates'}
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'current'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>

            {/* Option 2: Custom Date Range */}
            <Box
              onClick={() => setExportDateMode('custom')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'custom' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'custom' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'custom' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <DateRange sx={{ fontSize: 16, color: exportDateMode === 'custom' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  Custom Date Range
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>
                  Choose from & to dates
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'custom'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>

            {/* Option 3: Single Day */}
            <Box
              onClick={() => setExportDateMode('single')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'single' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'single' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'single' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <Today sx={{ fontSize: 16, color: exportDateMode === 'single' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  Single Day
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>
                  Export for one specific date
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'single'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>

            {/* Option 4: All Dates (Full History) */}
            <Box
              onClick={() => setExportDateMode('all')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'all' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'all' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'all' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <CalendarMonth sx={{ fontSize: 16, color: exportDateMode === 'all' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  All Data (Full History)
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>
                  Export full database history without any filters
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'all'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>
          </Box>

          {/* Date inputs — compact animated */}
          {exportDateMode === 'custom' && (
            <Box sx={{
              display: 'flex', gap: 1.5, mt: 1.5,
              p: 1.5, borderRadius: 2,
              bgcolor: '#fafbff', border: '1px solid #e8eaf6',
              animation: 'fadeIn 0.2s ease',
              '@keyframes fadeIn': { '0%': { opacity: 0, transform: 'translateY(-4px)' }, '100%': { opacity: 1, transform: 'translateY(0)' } },
            }}>
              <TextField
                label="From Date" type="date"
                value={exportFromDate}
                onChange={(e) => setExportFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
              />
              <TextField
                label="To Date" type="date"
                value={exportToDate}
                onChange={(e) => setExportToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
              />
            </Box>
          )}

          {exportDateMode === 'single' && (
            <Box sx={{
              mt: 1.5, p: 1.5, borderRadius: 2,
              bgcolor: '#fafbff', border: '1px solid #e8eaf6',
              animation: 'fadeIn 0.2s ease',
              '@keyframes fadeIn': { '0%': { opacity: 0, transform: 'translateY(-4px)' }, '100%': { opacity: 1, transform: 'translateY(0)' } },
            }}>
              <TextField
                label="Select Date" type="date"
                value={exportFromDate}
                onChange={(e) => setExportFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
              />
            </Box>
          )}
        </DialogContent>

        {/* Actions */}
        <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#fafbff', borderTop: '1px solid #eef2f6', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            onClick={handleCloseExportDialog}
            variant="outlined" size="small"
            sx={{
              borderRadius: 2, textTransform: 'none', fontWeight: 600,
              px: 2, fontSize: '0.8rem', borderColor: '#c5cae9', color: '#536DFE',
              '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83,109,254,0.04)' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmExport}
            variant="contained" size="small"
            disabled={exportLoading || (exportDateMode === 'custom' && (!exportFromDate || !exportToDate)) || (exportDateMode === 'single' && !exportFromDate)}
            startIcon={exportLoading ? <CircularProgress size={14} color="inherit" /> : <FileDownload sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: 2, textTransform: 'none', fontWeight: 700,
              px: 2.5, fontSize: '0.8rem',
              background: 'linear-gradient(135deg, #536DFE 0%, #3d5afe 100%)',
              boxShadow: '0 3px 10px rgba(83, 109, 254, 0.3)',
              '&:hover': { background: 'linear-gradient(135deg, #3d5afe 0%, #304ffe 100%)', boxShadow: '0 4px 14px rgba(83, 109, 254, 0.4)' },
              '&.Mui-disabled': { background: '#e0e0e0', boxShadow: 'none' },
            }}
          >
            {exportLoading ? 'Exporting...' : 'Export Now'}
          </Button>
        </Box>
      </Dialog>
      <AlertModal open={alertModal.open} title={alertModal.title} message={alertModal.message} onClose={closeAlert} />
    </Box>
  );
};

export default BranchWiseSummary;

